export type RouteAccess = 'public' | 'authenticated' | 'league-member' | 'league-admin';

export interface RouteDefinition {
  id: string;
  pattern: string;
  access: RouteAccess;
}

export interface MatchResult {
  route: RouteDefinition;
  params: Record<string, string>;
}

export const ROUTES: RouteDefinition[] = [
  { id: 'home', pattern: '/', access: 'public' },
  { id: 'join', pattern: '/join/:token', access: 'public' },
  { id: 'guest-portal', pattern: '/leagues/:leagueId/guest', access: 'public' },
  { id: 'league-list', pattern: '/leagues', access: 'authenticated' },
  { id: 'league-create', pattern: '/leagues/new', access: 'authenticated' },
  { id: 'standings', pattern: '/leagues/:leagueId/standings', access: 'league-member' },
  { id: 'matches', pattern: '/leagues/:leagueId/matches', access: 'league-member' },
  { id: 'roster', pattern: '/leagues/:leagueId/roster', access: 'league-member' },
  { id: 'match-detail', pattern: '/leagues/:leagueId/matches/:matchId', access: 'league-member' },
  { id: 'profile', pattern: '/leagues/:leagueId/profile', access: 'league-member' },
  { id: 'player-profile', pattern: '/leagues/:leagueId/players/:playerId', access: 'league-member' },
  { id: 'admin-home', pattern: '/leagues/:leagueId/admin', access: 'league-admin' },
  { id: 'admin-members', pattern: '/leagues/:leagueId/admin/members', access: 'league-admin' },
  { id: 'admin-seasons', pattern: '/leagues/:leagueId/admin/seasons', access: 'league-admin' },
  { id: 'match-days', pattern: '/leagues/:leagueId/match-days', access: 'league-member' },
  { id: 'match-day', pattern: '/leagues/:leagueId/match-days/:matchDayId', access: 'league-member' },
  {
    id: 'admin-season-players',
    pattern: '/leagues/:leagueId/admin/seasons/:seasonId/players',
    access: 'league-admin',
  },
  { id: 'admin-courses', pattern: '/leagues/:leagueId/admin/courses', access: 'league-admin' },
  { id: 'admin-score-entry', pattern: '/leagues/:leagueId/admin/score-entry', access: 'league-admin' },
  { id: 'admin-match-days', pattern: '/leagues/:leagueId/admin/match-days', access: 'league-admin' },
  {
    id: 'admin-match-day',
    pattern: '/leagues/:leagueId/admin/match-days/:matchDayId',
    access: 'league-admin',
  },
  { id: 'admin-invites', pattern: '/leagues/:leagueId/admin/invites', access: 'league-admin' },
];

function normalizePath(path: string): string {
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}

function split(path: string): string[] {
  return normalizePath(path).split('/').filter(Boolean);
}

export function matchRoute(path: string): MatchResult | null {
  const inputParts = split(path);

  for (const route of ROUTES) {
    const patternParts = split(route.pattern);
    if (patternParts.length !== inputParts.length) {
      continue;
    }

    const params: Record<string, string> = {};
    let matched = true;
    for (let index = 0; index < patternParts.length; index += 1) {
      const patternPart = patternParts[index]!;
      const inputPart = inputParts[index]!;

      if (patternPart.startsWith(':')) {
        params[patternPart.slice(1)] = inputPart;
        continue;
      }

      if (patternPart !== inputPart) {
        matched = false;
        break;
      }
    }

    if (matched) {
      return { route, params };
    }
  }

  return null;
}
